import { Box, Tooltip, Typography } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import React, { useEffect, useState } from 'react';
import { UserDto } from '../../../../Component/Model/Dto/User';
import { EmployeeHubRoles } from '../../../../Component/Common/Constants/Constants';
import { AdvancedSupplierModuleRole, BEEAdvisoryRole, EEAdvisoryRole, StrategicRelationshipManagerRole } from '../../../../Component/Common/Constants/UserRoles';
import { WarningDialog } from '../../../../Component/Common/Components/WarningDialog';
import { AdvisoryService } from '../../../../Component/Api/Advisory/AdvisoryService';

interface IProps {
    allRoles: UserDto.UserRoleViewModel[];
    userRoles: UserDto.UserRoleViewModel[];
    setUserRoles: React.Dispatch<React.SetStateAction<UserDto.UserRoleViewModel[]>>;
    showHasAccessView?: boolean;
    isRolesEditable?: boolean;
    isAdminRoleUser?: boolean;
    isSubAdminUser?: boolean;
    isEmployeeHubRoles?: boolean;
    userId?: string;
    disabledRoles?: string[];
    disabledRoleTooltips?: Record<string, string>;
}

export const UserRolesDataTable = (props: IProps) => {
    const groupedRoles: { [group: string]: UserDto.UserRoleViewModel[] } = {};

    props.allRoles.forEach((role) => {
        const group = role.Name.split('.')[0];
        if (!groupedRoles[group]) {
            groupedRoles[group] = [];
        }
        groupedRoles[group].push(role);
    });

    const [checkedRoles, setCheckedRoles] = useState<string[]>([]);
    const [checkAllEdit, setCheckAllEdit] = useState(false);
    const [checkAllView, setCheckAllView] = useState(false);
    const showDescription = false;

    const [showWarningDialog, setShowWarningDialog] = useState(false);
    const [warningMessage, setWarningMessage] = useState<string>('');    
    const [pendingRole, setPendingRole] = useState<string | null>(null);
    const [pendingChecked, setPendingChecked] = useState<boolean | null>(null);
    const [chosenEmployeeHubRole, setChosenEmployeeHubRole] = useState<string | null>(null);
    const employeeHubRoles = [AdvancedSupplierModuleRole, BEEAdvisoryRole, EEAdvisoryRole, StrategicRelationshipManagerRole];
    const advisoryRoles = [BEEAdvisoryRole, EEAdvisoryRole];

    const isRoleChecked = (role: string) => props.isEmployeeHubRoles === true ? checkedRoles.includes(role) : checkedRoles.includes(`${role}.edit`)

    useEffect(() => {
        const roles = props.userRoles.map((userRole) => userRole.Name);
        setCheckedRoles(roles);
    }, [props.userRoles]);

    useEffect(() => {
        if (props.isEmployeeHubRoles === true){
            const allEmployeeHubRoles = props.allRoles
                .filter(role => role.RoleGroup === EmployeeHubRoles)
                .every(role => checkedRoles.includes(role.Name));
            
            setCheckAllEdit(allEmployeeHubRoles);
        }
        else {
            const allEditRolesSelected = props.allRoles
                .filter(role => role.Name.endsWith('.edit') && !props.disabledRoles?.includes(role.Name))
                .every(role => checkedRoles.includes(role.Name));

            setCheckAllEdit(allEditRolesSelected);
        }

        const allViewRolesSelected = props.allRoles
            .filter(role => role.Name.endsWith('.view'))
            .every(role => checkedRoles.includes(role.Name));

        setCheckAllView(allViewRolesSelected);
    }, [checkedRoles, props.allRoles]);

    useEffect(() => {
        setChosenEmployeeHubRole(employeeHubRoles.find(r => isRoleChecked(r)) ?? null);
    }, [checkedRoles, props.isEmployeeHubRoles]);

    const updateRoleState = (name: string, checked: boolean) => {
        let roles = checkedRoles;

        if (name === 'PlatformPeopleSalary.view' && checked) {
            roles = [...roles, 'PlatformPeople.view'];
        }

        if (name === 'Registrations.edit' && !checked) {
            roles = roles.filter(r => r !== 'DeleteRegistrations');
        }

        if (name.endsWith('.edit') && checked) {
            const viewRole = `${name.split('.')[0]}.view`;
            roles = [...roles, viewRole];

            if (name.startsWith('TrainingRelatedSalaries')) {
                roles = [...roles, 'People.view'];
            }

            if (name.startsWith('EE123Salaries')) {
                roles = [...roles, 'EE123People.view'];
            }

            if (name.startsWith('PlatformPeopleSalary')) {
                roles = [...roles, 'PlatformPeople.view'];
            }
        }

        if (name.endsWith('.view') && !checked) {
            const editRole = `${name.split('.')[0]}.edit`;
            roles = roles.filter((role) => !role.includes(editRole));

            if (name.startsWith('People')) {
                roles = roles.filter((role) => !role.includes('TrainingRelatedSalaries'));
            }

            if (name.startsWith('EE123People')) {
                roles = roles.filter((role) => !role.includes('EE123Salaries'));
            }

            if (name.startsWith('PlatformPeople')) {
                roles = roles.filter((role) => !role.includes('PlatformPeopleSalary'));
            }
        }

        const updatedCheckedRoles = checked
            ? [...roles, name]
            : roles.filter((roleName) => roleName !== name);

        setCheckedRoles(updatedCheckedRoles);
        const updatedUserRoles = props.allRoles.filter((role) => updatedCheckedRoles.includes(role.Name));
        props.setUserRoles(updatedUserRoles);
    };

    const updateClientRoleState = async (name: string, checked: boolean) => {
        const isEmployeeHubRole = employeeHubRoles.some(r => name.includes(r));

        if (checked && isEmployeeHubRole && chosenEmployeeHubRole) {
            const isEmployeeLinked = await checkEmployeeLinks(chosenEmployeeHubRole);
            
            if (isEmployeeLinked) {
                setPendingRole(name);
                setPendingChecked(checked);
                setShowWarningDialog(true);
                return;
            }
        }

        if (!checked && isEmployeeHubRole) {
            const isEmployeeLinked = await checkEmployeeLinks(name);
            
            if (isEmployeeLinked) {
                setPendingRole(name);
                setPendingChecked(checked);
                setShowWarningDialog(true);
                return;
            }
        }
                
        proceedWithClientRoleState(name, checked);
    };

    const proceedWithClientRoleState = (name: string, checked: boolean) => {
        const roleNamesToAdd = props.isEmployeeHubRoles === true ? [name] : [`${name}.view`, `${name}.edit`];

        let nextChecked = [...checkedRoles];

        if (checked) {
            const isAdvisoryRole = advisoryRoles.includes(name);
            const rolesToRemove = isAdvisoryRole
                ? employeeHubRoles.filter(r => r !== name && !advisoryRoles.includes(r))
                : employeeHubRoles.filter(r => r !== name);

            nextChecked = nextChecked.filter(r => !rolesToRemove.includes(r));
            nextChecked = Array.from([...nextChecked, ...roleNamesToAdd]);
            setChosenEmployeeHubRole(name);
        } else {
            nextChecked = nextChecked.filter(r => r !== name);
            setChosenEmployeeHubRole(null);
        }

        setCheckedRoles(nextChecked);

        const updatedUserRoles = props.allRoles.filter((role) =>
            nextChecked.includes(role.Name)
        );

        props.setUserRoles(updatedUserRoles);
    };

    const checkEmployeeLinks = async (roleName: string): Promise<boolean> => {
        try {
            const clientLinks = await AdvisoryService.getLinkedBEE123Employees(props.userId!, roleName);
            const numberOfLinkedClients = clientLinks.data.numberOfLinksToClients;
            if (numberOfLinkedClients > 0) {
                const displayName = props.allRoles.find(r => r.Name === roleName)?.DisplayName ?? roleName;
                setWarningMessage(`This user is linked to ${numberOfLinkedClients} client registrations. Are you sure you want to remove the ${displayName} role?`);
                return true;
            }
        } catch (error) {
            console.error(error);
        }
        
        return false;
    };

    const handleCheckAllEdit = (checked: boolean) => {
        setCheckAllEdit(checked);

        if (checked) {
            setCheckAllView(checked);
        }

        const editRoles = props.allRoles.filter(role => role.Name.endsWith('.edit') && !props.disabledRoles?.includes(role.Name));
        const viewRoles = props.allRoles.filter(role => role.Name.endsWith('.view'));
        const deleteRole = props.allRoles.find(role => role.Name === 'DeleteRegistrations');

        const updatedUserRoles = checked
            ? [...editRoles, ...viewRoles, ...(deleteRole ? [deleteRole] : [])]
            : props.userRoles.filter(role => !
                editRoles.some(editRole => editRole.Name === role.Name)
                && (!deleteRole || role.Name !== deleteRole.Name));

        props.setUserRoles(updatedUserRoles);

        const updatedCheckedRoles = checked
            ? [...editRoles.map(role => role.Name), ...viewRoles.map(role => role.Name), ...(deleteRole ? [deleteRole.Name] : [])]
            : checkedRoles.filter(role =>
                !editRoles.some(editRole => editRole.Name === role)
                && (!deleteRole || role !== deleteRole.Name));

        setCheckedRoles(updatedCheckedRoles);
    };

    const handleCheckAllView = (checked: boolean) => {
        setCheckAllView(checked);

        if (!checked) {
            setCheckAllEdit(checked);
        }

        const editRoles = props.allRoles.filter(role => role.Name.endsWith('.edit'));
        const viewRoles = props.allRoles.filter(role => role.Name.endsWith('.view'));
        const deleteRole = props.allRoles.find(role => role.Name === 'DeleteRegistrations');

        const updatedUserRoles = checked
            ? [...props.userRoles.filter(role => editRoles.some(editRole => editRole.Name === role.Name)), ...viewRoles]
            : props.userRoles.filter(role => 
                !viewRoles.some(viewRole => viewRole.Name === role.Name) 
                && !editRoles.some(editRole => editRole.Name === role.Name) 
                && (!deleteRole || role.Name !== deleteRole.Name));

        props.setUserRoles(updatedUserRoles);

        const updatedCheckedRoles = checked
            ? [...checkedRoles.filter(role => !editRoles.some(editRole => editRole.Name === role)), ...viewRoles.map(role => role.Name)]
            : checkedRoles.filter(role => 
                !viewRoles.some(viewRole => viewRole.Name === role) 
                && !editRoles.some(editRole => editRole.Name === role) 
                && (!deleteRole || role !== deleteRole.Name));

        setCheckedRoles(updatedCheckedRoles);
    };

    const handleAcceptWarning = () => {
        if (pendingRole !== null && pendingChecked !== null) {
            proceedWithClientRoleState(pendingRole, pendingChecked);
        }
        resetPendingDialog();
    };

    const handleCancelWarning = () => {
        resetPendingDialog();
    };

    const resetPendingDialog = () => {
        setShowWarningDialog(false);
        setPendingRole(null);
        setPendingChecked(null);
    };

    const isDeleteRoleGroup = (groupRoles: UserDto.UserRoleViewModel[]) =>
        groupRoles.some(r => r.Name === 'DeleteRegistrations');    

    const shouldShowEditNotAvailable = (group: string): boolean => {
        return (group === 'LockScorecards' && !props.isSubAdminUser && !props.isAdminRoleUser);
    }

    const shouldShowViewNotAvailable = (group: string, groupRoles: UserDto.UserRoleViewModel[]): boolean => {
        return (group === 'LockScorecards' || isDeleteRoleGroup(groupRoles));
    }

    const StyledCheckbox = ({ disabled, checked, onChange }: { disabled?: boolean, checked: boolean; onChange: (event: React.ChangeEvent<HTMLInputElement>) => void }) => {
        return (
            <Checkbox
                sx={{
                    '&.MuiCheckbox-root .MuiSvgIcon-root': {
                        border: '0.0625rem solid #505B60',
                    },
                    '&.Mui-checked .MuiSvgIcon-root': {
                        border: 'transparent',
                        backgroundColor: '#505B60',
                    },
                    '&.Mui-disabled .MuiSvgIcon-root': {
                        backgroundColor: '#d1d1d1',
                    },
                    '&.Mui-disabled .Mui-checked .MuiSvgIcon-root': {
                        color: '#888888',
                    },
                }}
                disabled={disabled}
                checked={checked}
                onChange={onChange}
            />
        );
    };

    return (
        <>
        <table key={props.userRoles.length} style={{ width: '100%' }}>
            <thead>
                <tr style={{ background: "var(--semantics-disabled, #E9ECEF)" }}>
                    {!props.showHasAccessView ?
                        <>
                            <th>
                                {!props.isAdminRoleUser && (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <StyledCheckbox
                                            disabled={!props.isRolesEditable}
                                            checked={checkAllEdit}
                                            onChange={(e) => handleCheckAllEdit(e.target.checked)} />
                                        <Typography variant='body2'>Edit Access</Typography>
                                    </Box>
                                )}
                            </th>
                            <th>
                                {!props.isAdminRoleUser && (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <StyledCheckbox
                                            disabled={!props.isRolesEditable}
                                            checked={checkAllView}
                                            onChange={(e) => handleCheckAllView(e.target.checked)} />
                                        <Typography variant='body2'>View Access</Typography>
                                    </Box>
                                )}
                            </th>
                        </> :
                        <th>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <StyledCheckbox
                                    disabled={true}
                                    checked={employeeHubRoles.some(r => isRoleChecked(r))}
                                    onChange={() => {}} />
                                <Typography variant='body2'>Has Access</Typography>
                            </Box>

                        </th>}
                    <th><Typography variant='body2' ml={'7px'}>Role</Typography></th>
                    {showDescription && <th><Typography variant='body2' ml={'7px'}>Description</Typography></th>}
                </tr>
            </thead>
            <tbody>
                {Object.keys(groupedRoles).map((group, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #C9C9C980', padding: '10px 0 10px 0' }}>
                        {!props.isAdminRoleUser && (
                            <>
                                {!props.showHasAccessView ?
                                    <>
                                        <td width="150px">
                                            { shouldShowEditNotAvailable(group) ? (
                                                <Typography variant='body2' ml={'7px'}>Not Available</Typography>
                                            ) : (isDeleteRoleGroup(groupedRoles[group])) ? (
                                                <div> 
                                                    <StyledCheckbox
                                                        disabled={!checkedRoles.includes(`Registrations.edit`)}
                                                        checked={checkedRoles.includes(`${group}`)}
                                                        onChange={(e) => updateRoleState(`${group}`, e.target.checked)} />
                                                </div>
                                            ) : (
                                                <div>
                                                    <Tooltip
                                                        title={props.disabledRoles?.includes(`${group}.edit`) ? (props.disabledRoleTooltips?.[`${group}.edit`] ?? '') : ''}
                                                        placement="top"
                                                    >
                                                        <span>
                                                            <StyledCheckbox
                                                                disabled={!props.isRolesEditable || !!props.disabledRoles?.includes(`${group}.edit`)}
                                                                checked={checkedRoles.includes(`${group}.edit`)}
                                                                onChange={(e) => updateRoleState(`${group}.edit`, e.target.checked)} />
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                            )}
                                        </td>
                                        <td width="150px">
                                            { shouldShowViewNotAvailable(group, groupedRoles[group]) ? (
                                                <Typography variant='body2' ml={'7px'}>Not Available</Typography>
                                            ) : groupedRoles[group].length > 1 ? (
                                                <div>
                                                    <StyledCheckbox
                                                        disabled={!props.isRolesEditable}
                                                        checked={checkedRoles.includes(`${group}.view`)}
                                                        onChange={(e) => updateRoleState(`${group}.view`, e.target.checked)} />
                                                </div>
                                            ) : (
                                                <Typography variant='body2' ml={'7px'}>Not Available</Typography>
                                            )}
                                        </td>
                                    </> :
                                    <td width="150px">
                                        <div>
                                            <StyledCheckbox
                                                disabled={!props.isRolesEditable}
                                                checked={props.isEmployeeHubRoles === true
                                                    ? checkedRoles.includes(group)
                                                    : checkedRoles.includes(`${group}.edit`)}
                                                onChange={(e) => updateClientRoleState(group, e.target.checked)} />
                                        </div>
                                    </td>}
                            </>
                        )}
                        <td width="500px">
                            <Typography variant='body2' ml={'7px'}>{groupedRoles[group][0].DisplayName}</Typography>
                        </td>
                        {showDescription && <td width="300px">
                            <Typography variant='body2' ml={'7px'}>{groupedRoles[group][0].Description}</Typography>
                        </td>}
                    </tr>
                ))}
            </tbody>
        </table>
        <WarningDialog
            showDialog={showWarningDialog}
            handleAccept={handleAcceptWarning}
            handleCancel={handleCancelWarning}
            heading="Employee Hub Client Links"
            message={warningMessage} />
        </>
    );
};
